/**
 * The lifecycle mutation — ARCHITECTURE §05.6, the idempotency contract.
 *
 * **One key per user gesture, reused across every retry of that gesture,
 * discarded on success or on any change to the payload.**
 *
 * Each clause earns its place:
 *
 * * *Reused across retries* — a network blip after the server has already
 *   accepted the work is the case the explicit key exists for. Retrying with a
 *   **new** key would activate the SIMs twice; retrying with the same one
 *   replays the original job.
 * * *Discarded on success* — the next gesture is a genuinely different
 *   operation and must not replay the last one.
 * * **Discarded when the payload changes** — this is the subtle clause and the
 *   reason this is a contract rather than a convention. The backend answers
 *   **409 IDEMPOTENCY_MISMATCH** for the same key with a different body. So a
 *   user who submits, gets a validation error, *edits the form* and submits
 *   again would be met with a 409 that is entirely our fault. `submit()`
 *   therefore fingerprints the payload and re-mints on any change.
 *
 * A 409 is never auto-retried: it means our own bookkeeping is wrong, and
 * retrying a bookkeeping bug produces the same answer more often.
 */

import type { ClientJob } from '@rd/shared';
import { useCallback, useRef, useState } from 'react';

import { ApiClientError, apiRequest, newIdempotencyKey } from '../../../lib/apiClient.js';

import type { SimActionDef } from './actionRegistry.js';

export interface SimActionItem {
  msisdn: string;
  iccid?: string;
  eid?: string;
  planCode?: string;
}

export interface SimActionInput {
  items?: SimActionItem[];
  reason?: string;
  consent?: boolean;
  /** swap only — the API takes a SIM pair, not an items[] array. */
  existingIccid?: string;
  newIccid?: string;
  otp?: string;
}

export interface UseSimActionResult {
  submit(input: SimActionInput): Promise<ClientJob>;
  isSubmitting: boolean;
  error: ApiClientError | null;
  fieldErrors: Record<string, string>;
  /** Stable for the life of one gesture; survives retries. */
  idempotencyKey: string;
  /** Discards the key so the next submit is a genuinely new operation. */
  reset(): void;
}

/**
 * A stable fingerprint of the request body.
 *
 * Key order must not matter (two spellings of the same request are the same
 * request) but array order must (a different SIM order is a different request
 * to the upstream). `JSON.stringify` over sorted keys gives exactly that.
 */
export function payloadFingerprint(value: unknown): string {
  const canonical = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canonical);
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>)
          .filter(([, val]) => val !== undefined)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, val]) => [k, canonical(val)]),
      );
    }
    return v;
  };
  return JSON.stringify(canonical(value));
}

export function useSimAction(action: SimActionDef): UseSimActionResult {
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [idempotencyKey, setKey] = useState<string>(() => newIdempotencyKey());

  // The body the current key was minted for. `null` means "not yet used".
  const keyedFingerprint = useRef<string | null>(null);

  const reset = useCallback(() => {
    setKey(newIdempotencyKey());
    keyedFingerprint.current = null;
    setError(null);
  }, []);

  const submit = useCallback(
    async (input: SimActionInput): Promise<ClientJob> => {
      const body = buildBody(action, input);
      const fingerprint = payloadFingerprint(body);

      // The payload changed under an already-used key: mint a new one rather
      // than walk into a 409 we caused.
      let key = idempotencyKey;
      if (keyedFingerprint.current !== null && keyedFingerprint.current !== fingerprint) {
        key = newIdempotencyKey();
        setKey(key);
      }
      keyedFingerprint.current = fingerprint;

      setSubmitting(true);
      setError(null);
      try {
        const job = await apiRequest<ClientJob>(action.path, {
          method: 'POST',
          body,
          idempotencyKey: key,
        });
        // The gesture completed. The next one is a new operation.
        setKey(newIdempotencyKey());
        keyedFingerprint.current = null;
        return job;
      } catch (err) {
        if (err instanceof ApiClientError) setError(err);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [action, idempotencyKey],
  );

  return {
    submit,
    isSubmitting,
    error,
    fieldErrors: error?.fieldErrors() ?? {},
    idempotencyKey,
    reset,
  };
}

/**
 * Shapes the request body for one action.
 *
 * Swap is the exception the API itself makes: it takes a SIM pair rather than a
 * list, because the upstream operation is SIM-to-SIM.
 */
export function buildBody(action: SimActionDef, input: SimActionInput): Record<string, unknown> {
  if (action.key === 'swap') {
    return {
      existingIccid: input.existingIccid ?? '',
      newIccid: input.newIccid ?? '',
      ...(input.reason ? { reason: input.reason } : {}),
      ...(input.otp ? { otp: input.otp } : {}),
    };
  }

  const items = (input.items ?? []).map((item) => ({
    msisdn: item.msisdn,
    ...(item.iccid ? { iccid: item.iccid } : {}),
    ...(item.eid ? { eid: item.eid } : {}),
    ...(action.requiresPlan && item.planCode ? { planCode: item.planCode } : {}),
  }));

  return {
    items,
    ...(action.requiresReason && input.reason ? { reason: input.reason } : {}),
    ...(action.offersConsent && input.consent !== undefined ? { consent: input.consent } : {}),
  };
}
